import * as Sentry from "@sentry/nextjs";
import type { ISendFormResponse, ISendFormResponseError } from "api/sendForm";
import { handleConsent } from "app/form/[formId]/(formTemplate)/firstStep/helpers";
import type { IPersonalData } from "forms/helpers/extractPersonalData";
import type { ICandidateData } from "types/candidateData";
import type { IIncorrectInputDataError } from "types/formErrors";
import { predefinedFields } from "types/predefinedFields";
import type { CompensationPackage } from "types/salaryRange";
import { isFetchErrorBody } from "utils/helpers/fetchError";
import { logger } from "utils/helpers/logger";
import { removeKeysByValue } from "utils/helpers/removeKeysByValue";
import { removeKeysFromObject } from "utils/helpers/removeKeysFromObject";
import type { IRequest } from "utils/request";

interface PrepareSubmitDataParams {
    data: Record<string, unknown>;
    oid: string | null;
    ruid: string | null;
    exsysrecid: string | null;
    skkcfg: string | null;
    protocolOfferid: string | null;
    consentKeys: string[] | undefined;
    compensationPackage?: CompensationPackage | null;
}

export function prepareSubmitData({
    data,
    oid,
    ruid,
    exsysrecid,
    skkcfg,
    protocolOfferid,
    consentKeys,
    compensationPackage,
}: PrepareSubmitDataParams) {
    const consentsData = handleConsent({
        keys: consentKeys,
        formData: data,
    });

    const updatedData = removeKeysFromObject(data, consentKeys);
    const nullRemoved = removeKeysByValue(updatedData, (val) => val === null);

    const formData = {
        ...nullRemoved,
    };

    if (formData?.repeatEmail) {
        formData.repeatEmail = undefined;
    }

    if (consentsData) {
        Object.assign(formData, { consents: consentsData });
    }

    if (oid) {
        Object.assign(formData, { pracujofferid: oid });
    }
    if (protocolOfferid) {
        Object.assign(formData, {
            theProtocolOfferId: protocolOfferid,
        });
    }
    if (ruid) {
        Object.assign(formData, { ruid });
    }

    if (exsysrecid) {
        Object.assign(formData, { exsysrecid });
    }

    if (skkcfg) {
        Object.assign(formData, { skkcfg });
    }

    if (compensationPackage?.showCompensationPackage) {
        const payload: { salaryRanges?: typeof compensationPackage.salaryRanges; benefits?: string } = {};

        if (compensationPackage.salaryRanges && compensationPackage.salaryRanges.length > 0) {
            payload.salaryRanges = compensationPackage.salaryRanges.map((range) => ({
                minAmount: range.minAmount ?? null,
                maxAmount: range.maxAmount ?? null,
                currencyId: range.currencyId ?? null,
                salaryTypeId: range.salaryTypeId ?? null,
                contractTypeId: range.contractTypeId ?? null,
            }));
        }

        if (compensationPackage.benefits) {
            payload.benefits = compensationPackage.benefits;
        }

        if (payload.salaryRanges || payload.benefits) {
            Object.assign(formData, { compensationPackage: payload });
        }
    }

    return formData;
}

export function checkErrors(
    resp: IRequest<ISendFormResponse, ISendFormResponseError>,
    onError: (fieldName: string, e: IIncorrectInputDataError) => void,
) {
    if (!resp.isSuccess && isFetchErrorBody<ISendFormResponseError>(resp.error)) {
        const { code, details } = resp.error.data;

        if (code === "IncorrectInputData") {
            details.errors.forEach((e) => {
                let { fieldName } = e;

                // api returns cvFiles[index]
                if (fieldName.includes("cvFiles")) fieldName = predefinedFields.cvFiles;

                onError(fieldName, e);
            });
        }
    }
}

export const prepareFormDefaultValues = (clausesKeys: string[] | undefined, candidateData: ICandidateData | null) => {
    return {
        [predefinedFields.clauses]: clausesKeys ?? [],
        [predefinedFields.firstName]: candidateData?.firstName,
        [predefinedFields.lastName]: candidateData?.lastName,
        [predefinedFields.email]: candidateData?.email,
        [predefinedFields.phone]: candidateData?.phone,
        [predefinedFields.cvFiles]: candidateData?.fileId && [{ id: candidateData?.fileId }],
    };
};

export const checkIfApplyWithCv = (formData: Record<string, unknown>) => {
    const cvFiles = formData[predefinedFields.cvFiles];
    return Array.isArray(cvFiles) && cvFiles.length > 0;
};

export const hasRequiredPersonalFields = (personalData: IPersonalData): boolean =>
    !!personalData && !!personalData?.email && !!personalData?.firstName && !!personalData?.lastName;

export function warnIfSkkcfgOmitted({
    formId,
    rawSkkcfg,
    skkcfg,
    referrer,
}: {
    formId: string;
    rawSkkcfg: string | null;
    skkcfg: string | null;
    referrer?: string | null;
}) {
    if (!rawSkkcfg?.trim() || skkcfg) {
        return;
    }

    const referrerValue = referrer?.trim() || null;

    logger.log("[skkcfg] invalid GUID, omitted from submit", { formId, rawSkkcfg, referrer: referrerValue });
    Sentry.withScope((scope) => {
        scope.setTag("feature", "invalid-skkcfg");
        scope.setContext("query", { formId, skkcfg: rawSkkcfg, referrer: referrerValue });
        Sentry.captureMessage("Invalid skkcfg query param omitted from submission");
    });
}
